import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { query, collection, where, getDocs, doc, limit, updateDoc, arrayUnion, getDoc, startAfter, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { 
    fetchClientesYTiendasConCache, 
    fetchAOtherdminData, 
    clearUserCache, 
    clearAdminCache 
} from './firestoreService';
import { useAlert } from './utils/alerts';
import adminProfiler from './utils/AdminPerformanceProfiler';
import { AuthContext } from '../../context/AuthContext';
import {
    TextField,
    Button,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    Checkbox,
    ListItemText,
    CircularProgress,
    Alert,
    Box,
    Typography,
    Skeleton,
    Collapse,
    IconButton,
    Tooltip,
    InputAdornment,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import { ExpandMore, ExpandLess, Search, Clear } from '@mui/icons-material';
import "./styles/AddUser.scss";
import { comunidades, provincias, provinciasPorComunidad, getProvinciaFromCodigo, getComunidadFromCodigoProvincia } from "../../zoneData.js"
import ProvinciasSelect from './utils/ProvinciasSelect.js';
import { useNavigate } from 'react-router-dom';
import ClienteSearch from '../../utils/ClienteSearch';
import ServicioSearch from '../../utils/ServicioSearch';
import { procesarClientesFreematica } from '../../utils/ClienteSearch';
import PropTypes from 'prop-types';

/*
 * 🔥 ÍNDICES NECESARIOS EN FIREBASE:
 * 
 * Para clientes (servicios/{servicioId}/clientes):
 * - nombre (ascending) - para búsqueda por nombre
 * 
 * Para servicios (servicios/{servicioId}/tiendas):
 * - numTienda (ascending) - para ordenamiento y búsqueda por número
 * - nombreTienda (ascending) - para búsqueda por nombre
 * 
 * Crear estos índices en Firebase Console para que funcionen las búsquedas
 */

const EditAdmin = ({ adminId }) => {
    const [adminData, setAdminData] = useState({
        nombre: '',
        apellido: '',
        correo: '',
        tipoUsuario: [],
        clientesAsignados: [],
        tiendasAsignadas: [],
        provinciasAsignadas: [],
        comunidadesAsignadas: []
    });
    
    // Estados base
    const [clientes, setClientes] = useState([]);
    const [tiendas, setTiendas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isUsingCache, setIsUsingCache] = useState(false);
    const [provinciasSeleccionadas, setProvinciasSeleccionadas] = useState([]);
    const [comunidadesSeleccionadas, setComunidadesSeleccionadas] = useState([]);
    const [newTipoUsuario, setNewTipoUsuario] = useState([]);
    
    // Estados para UI de secciones expandibles
    const [showClientes, setShowClientes] = useState(false);
    const [showServicios, setShowServicios] = useState(false);
    
    // Estados para lazy loading de clientes
    const [clientesData, setClientesData] = useState([]);
    const [clientesLoading, setClientesLoading] = useState(false);
    const [clientesHasMore, setClientesHasMore] = useState(true);
    const [clientesLastDoc, setClientesLastDoc] = useState(null);
    const [clientesSearchTerm, setClientesSearchTerm] = useState('');
    const [clientesSearchDebounce, setClientesSearchDebounce] = useState('');
    
    // Estados para lazy loading de servicios
    const [serviciosData, setServiciosData] = useState([]);
    const [serviciosLoading, setServiciosLoading] = useState(false);
    const [serviciosHasMore, setServiciosHasMore] = useState(true);
    const [serviciosLastDoc, setServiciosLastDoc] = useState(null);
    const [serviciosSearchTerm, setServiciosSearchTerm] = useState('');
    const [serviciosSearchDebounce, setServiciosSearchDebounce] = useState('');
    
    // Estado para toggle de visualización (Localización/Nombre)
    const [serviciosDisplayMode, setServiciosDisplayMode] = useState('nombre');
    
    const handleServiciosDisplayModeChange = (event, newMode) => {
        if (newMode !== null) {
            setServiciosDisplayMode(newMode);
        }
    };
    
    // Referencias para observadores de scroll
    const clientesObserverRef = useRef(null);
    const serviciosObserverRef = useRef(null);
    
    // Constantes
    const ITEMS_PER_PAGE = 50;
    const SEARCH_DEBOUNCE_MS = 500;

    const { currentUser, currentService } = useContext(AuthContext);
    const navigate = useNavigate();

    // Cargar datos iniciales
    useEffect(() => {
        if (currentService && adminId) {
            setInitialLoading(true);
            setError(null);
            setIsUsingCache(false);

            const loadInitialData = async () => {
                try {
                    // 📊 Iniciar sesión de profiling
                    adminProfiler.startSession('EditAdmin', {
                        adminId,
                        serviceId: currentService?.id
                    });

                    const startTime = Date.now();
                    
                    // 📊 Medir carga de datos del admin
                    adminProfiler.startMeasurement('01_FetchAdminData', {
                        adminId
                    });
                    
                    // Solo cargar datos del admin, NO cargar todos los clientes/tiendas
                    const adminDataResult = await fetchAOtherdminData(adminId, currentService);

                    const loadTime = Date.now() - startTime;
                    
                    const usingCache = loadTime < 100;
                    if (usingCache) {
                        setIsUsingCache(true);
                        setTimeout(() => setIsUsingCache(false), 3000);
                    }

                    // Procesar datos del admin
                    const processedAdminData = {
                        ...adminDataResult,
                        clientesAsignados: adminDataResult.clientes || [],
                        tiendasAsignadas: adminDataResult.tiendas || [],
                        tipoUsuario: adminDataResult.tipoUsuario || [],
                        provinciasAsignadas: adminDataResult.provincias || [],
                        comunidadesAsignadas: adminDataResult.comunidades || []
                    };

                    adminProfiler.endMeasurement('01_FetchAdminData', {
                        loaded: true,
                        nombre: processedAdminData.nombre,
                        clientesCount: processedAdminData.clientesAsignados?.length || 0,
                        tiendasCount: processedAdminData.tiendasAsignadas?.length || 0,
                        usingCache
                    });

                    // 📊 Medir renderizado del formulario
                    adminProfiler.startMeasurement('02_RenderForm');

                    setAdminData(processedAdminData);
                    
                    console.log('✅ Datos iniciales cargados correctamente');
                    console.log('🔥 Admin Data:', {
                        id: processedAdminData.id,
                        nombre: processedAdminData.nombre,
                        clientesAsignados: processedAdminData.clientesAsignados?.length || 0,
                        tiendasAsignadas: processedAdminData.tiendasAsignadas?.length || 0,
                        tipoUsuario: processedAdminData.tipoUsuario
                    });
                    
                    // Inicializar provincias y comunidades
                    setProvinciasSeleccionadas(processedAdminData.provinciasAsignadas || []);
                    setComunidadesSeleccionadas(processedAdminData.comunidadesAsignadas || []);
                    
                    console.log('✅ Datos iniciales cargados correctamente');
                    
                    // Esperar renderizado
                    setTimeout(() => {
                        adminProfiler.endMeasurement('02_RenderForm', {
                            formFieldsRendered: true
                        });
                        
                        // 📊 Finalizar sesión
                        adminProfiler.endSession({
                            totalClientes: processedAdminData.clientesAsignados?.length || 0,
                            totalTiendas: processedAdminData.tiendasAsignadas?.length || 0,
                            usingCache,
                            cacheImprovement: usingCache ? '~90%' : 'N/A',
                            loadTime: `${loadTime}ms`
                        });
                    }, 0);
                    
                } catch (error) {
                    console.error('❌ Error loading data:', error);
                    setError('Error al cargar los datos del usuario. Por favor, intenta de nuevo.');
                    
                    // Finalizar sesión con error
                    adminProfiler.endSession({
                        error: error.message,
                        success: false
                    });
                } finally {
                    setInitialLoading(false);
                }
            };

            loadInitialData();
        }
    }, [currentService, adminId]);

    // Cargar clientes cuando se expande la sección
    useEffect(() => {
        if (!currentService || !adminData.clientesAsignados) return;
        
        if (showClientes && clientesData.length === 0 && !clientesLoading) {
            console.log('🔄 Sección clientes expandida, cargando datos iniciales...');
            loadClientes('', true);
        }
    }, [showClientes, currentService, adminData.clientesAsignados]);

    // Cargar servicios cuando se expande la sección
    useEffect(() => {
        if (!currentService || !adminData.tiendasAsignadas) return;
        
        if (showServicios && serviciosData.length === 0 && !serviciosLoading) {
            console.log('🔄 Sección servicios expandida, cargando datos iniciales...');
            loadServicios('', true);
        }
    }, [showServicios, currentService, adminData.tiendasAsignadas]);

    // Debounce para búsqueda de clientes
    useEffect(() => {
        console.log('⏱️ Debounce clientes - término original:', clientesSearchTerm);
        const timeout = setTimeout(() => {
            console.log('⏱️ Debounce clientes - aplicando término:', clientesSearchTerm);
            setClientesSearchDebounce(clientesSearchTerm);
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timeout);
    }, [clientesSearchTerm]);

    // Debounce para búsqueda de servicios
    useEffect(() => {
        console.log('⏱️ Debounce servicios - término original:', serviciosSearchTerm);
        const timeout = setTimeout(() => {
            console.log('⏱️ Debounce servicios - aplicando término:', serviciosSearchTerm);
            setServiciosSearchDebounce(serviciosSearchTerm);
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timeout);
    }, [serviciosSearchTerm]);

    // Ejecutar búsqueda de clientes cuando cambie el término
    useEffect(() => {
        if (!currentService) return;
        
        if (clientesSearchDebounce.trim()) {
            console.log('🔄 Ejecutando búsqueda de clientes:', clientesSearchDebounce);
            loadClientes(clientesSearchDebounce, true);
        }
    }, [clientesSearchDebounce, currentService]); // NO incluir adminData.clientesAsignados para evitar reset al seleccionar

    // Ejecutar búsqueda de servicios cuando cambie el término
    useEffect(() => {
        if (!currentService) return;
        
        if (serviciosSearchDebounce.trim()) {
            console.log('🔄 Ejecutando búsqueda de servicios:', serviciosSearchDebounce);
            loadServicios(serviciosSearchDebounce, true);
        }
    }, [serviciosSearchDebounce, currentService]); // NO incluir adminData.tiendasAsignadas para evitar reset al seleccionar

    // Función para cargar clientes con Firebase query
    const loadClientes = useCallback(async (searchTerm = '', reset = false) => {
        if (!currentService || clientesLoading) return;
        
        console.log('🔍 Cargando clientes:', { searchTerm, reset, hasMore: clientesHasMore });
        
        if (!reset && !clientesHasMore) return;
        
        setClientesLoading(true);
        
        try {
            const clientesRef = collection(db, 'servicios', currentService.id, 'clientes');
            let q;
            
            if (searchTerm.trim()) {
                // BÚSQUEDA EN FIREBASE: Buscar en toda la colección, no solo en datos cargados
                q = query(
                    clientesRef,
                    where('nombre', '>=', searchTerm.trim()),
                    where('nombre', '<=', searchTerm.trim() + '\uf8ff'),
                    orderBy('nombre'),
                    limit(ITEMS_PER_PAGE),
                    ...(reset || !clientesLastDoc ? [] : [startAfter(clientesLastDoc)])
                );
                console.log('🔍 BÚSQUEDA EN FIREBASE por nombre:', searchTerm.trim());
                console.log('🔥 Firebase Query Clientes (Búsqueda):', {
                    collection: `servicios/${currentService.id}/clientes`,
                    where: [
                        { field: 'nombre', operator: '>=', value: searchTerm.trim() },
                        { field: 'nombre', operator: '<=', value: searchTerm.trim() + '\uf8ff' }
                    ],
                    orderBy: 'nombre',
                    limit: ITEMS_PER_PAGE,
                    startAfter: reset || !clientesLastDoc ? 'N/A' : clientesLastDoc.id
                });
            } else {
                // CARGA INICIAL: Solo los primeros 50 clientes para mostrar
                q = query(
                    clientesRef,
                    orderBy('nombre'),
                    limit(ITEMS_PER_PAGE),
                    ...(reset || !clientesLastDoc ? [] : [startAfter(clientesLastDoc)])
                );
                console.log('📋 CARGA INICIAL: Solo primeros 50 clientes');
                console.log('🔥 Firebase Query Clientes (Inicial):', {
                    collection: `servicios/${currentService.id}/clientes`,
                    orderBy: 'nombre',
                    limit: ITEMS_PER_PAGE,
                    startAfter: reset || !clientesLastDoc ? 'N/A' : clientesLastDoc.id
                });
            }
            
            console.log('⚡ Ejecutando query de clientes...');
            console.log('🔥 Query completa:', q);
            const snapshot = await getDocs(q);
            console.log('📊 Clientes obtenidos:', snapshot.docs.length);
            console.log('🔥 Documentos Firestore:', snapshot.docs.map(doc => ({ id: doc.id, nombre: doc.data().nombre })));
            
            const newClientes = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                isAsignado: adminData.clientesAsignados.includes(doc.id)
            }));
            
            // Actualizar estado
            if (reset) {
                setClientesData(newClientes);
            } else {
                setClientesData(prev => [...prev, ...newClientes]);
            }
            
            // Actualizar paginación
            const lastDoc = snapshot.docs[snapshot.docs.length - 1];
            setClientesLastDoc(lastDoc);
            setClientesHasMore(snapshot.docs.length === ITEMS_PER_PAGE);
            
            console.log('✅ Clientes cargados:', newClientes.length, 'hasMore:', snapshot.docs.length === ITEMS_PER_PAGE);
            
        } catch (error) {
            console.error('❌ Error loading clientes:', error);
            console.error('🔥 Error Firebase:', {
                error: error.message,
                code: error.code,
                details: error
            });
        } finally {
            setClientesLoading(false);
        }
    }, [currentService, adminData.clientesAsignados, clientesLoading, clientesHasMore, clientesLastDoc]);

    // Función para cargar servicios con Firebase query
    const loadServicios = useCallback(async (searchTerm = '', reset = false) => {
        if (!currentService || serviciosLoading) return;
        
        console.log('🔍 Cargando servicios:', { searchTerm, reset, hasMore: serviciosHasMore });
        
        if (!reset && !serviciosHasMore) return;
        
        setServiciosLoading(true);
        
        try {
            const tiendasRef = collection(db, 'servicios', currentService.id, 'tiendas');
            let snapshots = [];
            
            if (searchTerm.trim()) {
                const searchTermTrimmed = searchTerm.trim().toLowerCase();
                
                // Cargar más datos para búsqueda y filtrar client-side
                const q = query(
                    tiendasRef,
                    orderBy('numTienda'),
                    limit(200)
                );
                
                console.log('🔍 BÚSQUEDA: Cargando servicios para filtrar por:', searchTermTrimmed);
                const snapshot = await getDocs(q);
                
                // Filtrar client-side para búsqueda parcial en múltiples campos
                snapshots = snapshot.docs.filter(doc => {
                    const data = doc.data();
                    const numTienda = data.numTienda?.toString().toLowerCase() || '';
                    const nombreTienda = data.nombreTienda?.toLowerCase() || '';
                    const localizacion = data.localizacion?.toLowerCase() || '';
                    const nombreCliente = data.nombreCliente?.toLowerCase() || '';
                    
                    return numTienda.includes(searchTermTrimmed) ||
                           nombreTienda.includes(searchTermTrimmed) ||
                           localizacion.includes(searchTermTrimmed) ||
                           nombreCliente.includes(searchTermTrimmed);
                });
                
                console.log('📊 Servicios filtrados:', snapshots.length, 'de', snapshot.docs.length);
            } else {
                // CARGA INICIAL: Solo los primeros 50 servicios para mostrar
                const q = query(
                    tiendasRef,
                    orderBy('numTienda'),
                    limit(ITEMS_PER_PAGE),
                    ...(reset || !serviciosLastDoc ? [] : [startAfter(serviciosLastDoc)])
                );
                console.log('📋 CARGA INICIAL: Solo primeros 50 servicios');
                const snapshot = await getDocs(q);
                snapshots = snapshot.docs;
                
                // Actualizar paginación solo para carga inicial
                const lastDoc = snapshot.docs[snapshot.docs.length - 1];
                setServiciosLastDoc(lastDoc);
                setServiciosHasMore(snapshot.docs.length === ITEMS_PER_PAGE);
            }
            
            console.log('⚡ Ejecutando query de servicios...');
            console.log('📊 Servicios obtenidos:', snapshots.length);
            
            // Deduplicar por id
            const seenIds = new Set();
            const newServicios = snapshots
                .filter(doc => {
                    if (seenIds.has(doc.id)) return false;
                    seenIds.add(doc.id);
                    return true;
                })
                .map(doc => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        numTienda: data.numTienda,
                        nombreTienda: data.nombreTienda || `Servicio ${data.numTienda}`,
                        nombreCliente: data.nombreCliente || 'Cliente desconocido',
                        localizacion: data.localizacion || '',
                        isAsignado: adminData.tiendasAsignadas?.includes(doc.id) || false,
                        ...data
                    };
                });
            
            // Actualizar estado
            if (reset) {
                setServiciosData(newServicios);
            } else {
                setServiciosData(prev => [...prev, ...newServicios]);
            }
            
            console.log('✅ Servicios cargados:', newServicios.length, 'hasMore:', serviciosHasMore);
            
        } catch (error) {
            console.error('❌ Error loading servicios:', error);
            console.error('🔥 Error Firebase:', {
                error: error.message,
                code: error.code,
                details: error
            });
        } finally {
            setServiciosLoading(false);
        }
    }, [currentService, adminData.tiendasAsignadas, serviciosLoading, serviciosHasMore, serviciosLastDoc]);



    // Observer para scroll infinito de clientes
    useEffect(() => {
        if (!currentService || !adminData.clientesAsignados) return;
        
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && clientesHasMore && !clientesLoading) {
                    console.log('📜 Cargando más clientes por scroll...');
                    // Usar la función loadClientes del scope
                    const loadMoreClientes = async () => {
                        if (!currentService || clientesLoading) return;
                        
                        setClientesLoading(true);
                        try {
                            const clientesRef = collection(db, 'servicios', currentService.id, 'clientes');
                            const q = query(
                                clientesRef,
                                orderBy('nombre'),
                                limit(ITEMS_PER_PAGE),
                                startAfter(clientesLastDoc)
                            );
                            
                            console.log('🔥 Firebase Query Clientes (Scroll):', {
                                collection: `servicios/${currentService.id}/clientes`,
                                orderBy: 'nombre',
                                limit: ITEMS_PER_PAGE,
                                startAfter: clientesLastDoc.id
                            });
                            
                            const snapshot = await getDocs(q);
                            console.log('🔥 Scroll - Clientes obtenidos:', snapshot.docs.length);
                            
                            const newClientes = snapshot.docs.map(doc => ({
                                id: doc.id,
                                ...doc.data(),
                                isAsignado: adminData.clientesAsignados.includes(doc.id)
                            }));
                            
                            setClientesData(prev => [...prev, ...newClientes]);
                            
                            const lastDoc = snapshot.docs[snapshot.docs.length - 1];
                            setClientesLastDoc(lastDoc);
                            setClientesHasMore(snapshot.docs.length === ITEMS_PER_PAGE);
                            
                        } catch (error) {
                            console.error('❌ Error loading more clientes:', error);
                            console.error('🔥 Error Firebase Scroll:', {
                                error: error.message,
                                code: error.code,
                                details: error
                            });
                        } finally {
                            setClientesLoading(false);
                        }
                    };
                    
                    loadMoreClientes();
                }
            },
            { threshold: 0.1 }
        );

        if (clientesObserverRef.current) {
            observer.observe(clientesObserverRef.current);
        }

        return () => {
            if (clientesObserverRef.current) {
                observer.unobserve(clientesObserverRef.current);
            }
        };
    }, [clientesHasMore, clientesLoading, clientesSearchDebounce, currentService, adminData.clientesAsignados, clientesLastDoc]);

    // Observer para scroll infinito de servicios
    useEffect(() => {
        if (!currentService || !adminData.tiendasAsignadas) return;
        
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && serviciosHasMore && !serviciosLoading) {
                    console.log('📜 Cargando más servicios por scroll...');
                    // Usar la función loadServicios del scope
                    const loadMoreServicios = async () => {
                        if (!currentService || serviciosLoading) return;
                        
                        setServiciosLoading(true);
                        try {
                            const tiendasRef = collection(db, 'servicios', currentService.id, 'tiendas');
                            const q = query(
                                tiendasRef,
                                orderBy('numTienda'),
                                limit(ITEMS_PER_PAGE),
                                startAfter(serviciosLastDoc)
                            );
                            
                            console.log('🔥 Firebase Query Servicios (Scroll):', {
                                collection: `servicios/${currentService.id}/tiendas`,
                                orderBy: 'numTienda',
                                limit: ITEMS_PER_PAGE,
                                startAfter: serviciosLastDoc.id
                            });
                            
                            const snapshot = await getDocs(q);
                            console.log('🔥 Scroll - Servicios obtenidos:', snapshot.docs.length);
                            
                            const newServicios = snapshot.docs.map(doc => {
                                const data = doc.data();
                                return {
                                    id: doc.id,
                                    numTienda: data.numTienda,
                                    nombreTienda: data.nombreTienda || `Servicio ${data.numTienda}`,
                                    nombreCliente: data.nombreCliente || 'Cliente desconocido',
                                    isAsignado: adminData.tiendasAsignadas.includes(doc.id),
                                    ...data
                                };
                            });
                            
                            setServiciosData(prev => [...prev, ...newServicios]);
                            
                            const lastDoc = snapshot.docs[snapshot.docs.length - 1];
                            setServiciosLastDoc(lastDoc);
                            setServiciosHasMore(snapshot.docs.length === ITEMS_PER_PAGE);
                            
                        } catch (error) {
                            console.error('❌ Error loading more servicios:', error);
                            console.error('🔥 Error Firebase Scroll:', {
                                error: error.message,
                                code: error.code,
                                details: error
                            });
                        } finally {
                            setServiciosLoading(false);
                        }
                    };
                    
                    loadMoreServicios();
                }
            },
            { threshold: 0.1 }
        );

        if (serviciosObserverRef.current) {
            observer.observe(serviciosObserverRef.current);
        }

        return () => {
            if (serviciosObserverRef.current) {
                observer.unobserve(serviciosObserverRef.current);
            }
        };
    }, [serviciosHasMore, serviciosLoading, serviciosSearchDebounce, currentService, adminData.tiendasAsignadas, serviciosLastDoc]);

    // Memoizar datos de clientes mostrados (incluyendo asignados al inicio)
    const clientesMostrados = useMemo(() => {
        if (!clientesData || clientesData.length === 0) {
            return [];
        }
        
        if (clientesSearchDebounce.trim()) {
            // Si hay búsqueda, mostrar resultados de búsqueda
            return clientesData;
        } else {
            // Si no hay búsqueda, mostrar primero los asignados y luego los demás
            const asignados = clientesData.filter(c => c.isAsignado);
            const noAsignados = clientesData.filter(c => !c.isAsignado);
            return [...asignados, ...noAsignados];
        }
    }, [clientesData, clientesSearchDebounce]);

    // Memoizar datos de servicios mostrados (incluyendo asignados al inicio)
    const serviciosMostrados = useMemo(() => {
        if (!serviciosData || serviciosData.length === 0) {
            return [];
        }
        
        if (serviciosSearchDebounce.trim()) {
            // Si hay búsqueda, mostrar resultados de búsqueda
            return serviciosData;
        } else {
            // Si no hay búsqueda, mostrar primero los asignados y luego los demás
            const asignados = serviciosData.filter(s => s.isAsignado);
            const noAsignados = serviciosData.filter(s => !s.isAsignado);
            return [...asignados, ...noAsignados];
        }
    }, [serviciosData, serviciosSearchDebounce]);

    // Función para manejar cambios en clientes
    const handleClienteToggle = useCallback((clienteId, isChecked) => {
        // Calcular nuevos clientes asignados
        const newClientesAsignados = isChecked 
            ? [...adminData.clientesAsignados, clienteId]
            : adminData.clientesAsignados.filter(id => id !== clienteId);
        
        // Actualizar estado del admin (solo localmente, se guardará al hacer clic en "Actualizar")
        setAdminData(prev => ({
            ...prev,
            clientesAsignados: newClientesAsignados
        }));

        // Actualizar estado de asignación en los datos mostrados
        setClientesData(prev => prev.map(cliente => 
            cliente.id === clienteId 
                ? { ...cliente, isAsignado: isChecked }
                : cliente
        ));
        
        console.log(`✅ Cliente ${clienteId} ${isChecked ? 'seleccionado' : 'deseleccionado'} (se guardará al hacer clic en Actualizar)`);
    }, [adminData.clientesAsignados]);

    // Función para manejar cambios en servicios
    const handleServicioToggle = useCallback((servicioId, isChecked) => {
        // Calcular nuevas tiendas asignadas
        const newTiendasAsignadas = isChecked 
            ? [...adminData.tiendasAsignadas, servicioId]
            : adminData.tiendasAsignadas.filter(id => id !== servicioId);
        
        // Actualizar estado del admin (solo localmente, se guardará al hacer clic en "Actualizar")
        setAdminData(prev => ({
            ...prev,
            tiendasAsignadas: newTiendasAsignadas
        }));

        // Actualizar estado de asignación en los datos mostrados
        setServiciosData(prev => prev.map(servicio => 
            servicio.id === servicioId 
                ? { ...servicio, isAsignado: isChecked }
                : servicio
        ));
        
        console.log(`✅ Servicio ${servicioId} ${isChecked ? 'seleccionado' : 'deseleccionado'} (se guardará al hacer clic en Actualizar)`);
    }, [adminData.tiendasAsignadas]);

    // Funciones para seleccionar todos
    const handleSelectAllClientes = useCallback((checked) => {
        if (checked) {
            const todosLosIds = clientesMostrados.map(cliente => cliente.id);
            setAdminData(prev => ({
                ...prev,
                clientesAsignados: [...new Set([...prev.clientesAsignados, ...todosLosIds])]
            }));
        } else {
            const idsADeseleccionar = clientesMostrados.map(cliente => cliente.id);
            setAdminData(prev => ({
                ...prev,
                clientesAsignados: prev.clientesAsignados.filter(id => !idsADeseleccionar.includes(id))
            }));
        }

        // Actualizar estado en datos mostrados
        setClientesData(prev => prev.map(cliente => ({
            ...cliente,
            isAsignado: checked
        })));
    }, [clientesMostrados]);

    const handleSelectAllServicios = useCallback((checked) => {
        if (checked) {
            const todosLosIds = serviciosMostrados.map(servicio => servicio.id);
            setAdminData(prev => ({
                ...prev,
                tiendasAsignadas: [...new Set([...prev.tiendasAsignadas, ...todosLosIds])]
            }));
        } else {
            const idsADeseleccionar = serviciosMostrados.map(servicio => servicio.id);
            setAdminData(prev => ({
                ...prev,
                tiendasAsignadas: prev.tiendasAsignadas.filter(id => !idsADeseleccionar.includes(id))
            }));
        }

        // Actualizar estado en datos mostrados
        setServiciosData(prev => prev.map(servicio => ({
            ...servicio,
            isAsignado: checked
        })));
    }, [serviciosMostrados]);

    // Función para asignar TODOS los clientes de la base de datos
    const handleAssignAllClientes = useCallback(async (checked) => {
        if (!currentService) return;
        
        try {
            if (checked) {
                console.log('🚀 Cargando TODOS los clientes de la base de datos...');
                
                // Obtener todos los clientes de la base de datos
                const clientesRef = collection(db, 'servicios', currentService.id, 'clientes');
                const q = query(clientesRef, orderBy('nombre'));
                const snapshot = await getDocs(q);
                
                const todosLosClientesIds = snapshot.docs.map(doc => doc.id);
                console.log(`📊 Total de clientes en la base de datos: ${todosLosClientesIds.length}`);
                
                // Actualizar estado del admin (solo localmente)
                setAdminData(prev => ({
                    ...prev,
                    clientesAsignados: todosLosClientesIds
                }));
                
                // Actualizar estado de los datos mostrados
                setClientesData(prev => prev.map(cliente => ({
                    ...cliente,
                    isAsignado: true
                })));
                
                console.log('✅ Todos los clientes seleccionados (se guardarán al hacer clic en Actualizar)');
                
            } else {
                console.log('🚀 Deseleccionando TODOS los clientes...');
                
                // Desasignar todos los clientes
                setAdminData(prev => ({
                    ...prev,
                    clientesAsignados: []
                }));
                
                // Actualizar estado de los datos mostrados
                setClientesData(prev => prev.map(cliente => ({
                    ...cliente,
                    isAsignado: false
                })));
                
                console.log('✅ Todos los clientes deseleccionados');
            }
        } catch (error) {
            console.error('❌ Error al cargar todos los clientes:', error);
            alert('Error al cargar todos los clientes. Por favor, intenta de nuevo.');
        }
    }, [currentService]);

    // Función para asignar TODOS los servicios de la base de datos
    const handleAssignAllServicios = useCallback(async (checked) => {
        if (!currentService) return;
        
        try {
            if (checked) {
                console.log('🚀 Cargando TODOS los servicios de la base de datos...');
                
                // Obtener todos los servicios de la base de datos
                const tiendasRef = collection(db, 'servicios', currentService.id, 'tiendas');
                const q = query(tiendasRef, orderBy('numTienda'));
                const snapshot = await getDocs(q);
                
                const todosLosServiciosIds = snapshot.docs.map(doc => doc.id);
                console.log(`📊 Total de servicios en la base de datos: ${todosLosServiciosIds.length}`);
                
                // Actualizar estado del admin (solo localmente)
                setAdminData(prev => ({
                    ...prev,
                    tiendasAsignadas: todosLosServiciosIds
                }));
                
                // Actualizar estado de los datos mostrados
                setServiciosData(prev => prev.map(servicio => ({
                    ...servicio,
                    isAsignado: true
                })));
                
                console.log('✅ Todos los servicios seleccionados (se guardarán al hacer clic en Actualizar)');
                
            } else {
                console.log('🚀 Deseleccionando TODOS los servicios...');
                
                // Desasignar todos los servicios
                setAdminData(prev => ({
                    ...prev,
                    tiendasAsignadas: []
                }));
                
                // Actualizar estado de los datos mostrados
                setServiciosData(prev => prev.map(servicio => ({
                    ...servicio,
                    isAsignado: false
                })));
                
                console.log('✅ Todos los servicios deseleccionados');
            }
        } catch (error) {
            console.error('❌ Error al cargar todos los servicios:', error);
            alert('Error al cargar todos los servicios. Por favor, intenta de nuevo.');
        }
    }, [currentService]);

    // Resto de funciones originales (sin cambios)
    const handleUpdate = useCallback(async () => {
        setLoading(true);
        setError(null);

        // 📊 Iniciar sesión de profiling para actualización
        adminProfiler.startSession('EditAdmin_Update', {
            adminId,
            serviceId: currentService.id
        });

        try {
            // 📊 Medir actualización en Firestore
            adminProfiler.startMeasurement('07_UpdateAdmin', {
                adminId,
                clientesCount: adminData.clientesAsignados.length,
                tiendasCount: adminData.tiendasAsignadas.length
            });

            const adminRef = doc(db, 'servicios', currentService.id, 'admins', adminId);
            const lowercaseEmail = adminData.correo.toLowerCase();

            if (newTipoUsuario.length > 0) {
                await updateDoc(adminRef, {
                    tipoUsuario: newTipoUsuario,
                });
            }

            await updateDoc(adminRef, {
                nombre: adminData.nombre,
                apellido: adminData.apellido || null,
                correo: lowercaseEmail,
                clientes: adminData.clientesAsignados,
                tiendas: adminData.tiendasAsignadas,
                provincias: adminData.provinciasAsignadas,
                comunidades: adminData.comunidadesAsignadas,
                clientePrincipal: adminData.clientesAsignados[0] || null,
                fechaConfiguracionAsignacion: new Date().toISOString(),
            });

            adminProfiler.endMeasurement('07_UpdateAdmin', {
                updated: true,
                fieldsUpdated: ['nombre', 'apellido', 'correo', 'clientes', 'tiendas', 'provincias', 'comunidades']
            });

            // NOTA: updateClientesYTiendas comentado temporalmente porque causa loops infinitos
            // cuando hay muchos clientes/tiendas asignados. Esta función actualiza cada documento
            // de cliente y tienda individualmente, lo cual puede llevar mucho tiempo.
            // TODO: Optimizar esta función o hacerla opcional
            /*
            await updateClientesYTiendas(
                adminId,
                newTipoUsuario.length > 0 ? newTipoUsuario : adminData.tipoUsuario,
                adminData.clientesAsignados,
                adminData.tiendasAsignadas
            );
            */

            // 📊 Medir limpieza de caché
            adminProfiler.startMeasurement('08_ClearCache');
            
            clearUserCache(currentService.id, adminId);
            clearAdminCache(currentService.id);
            
            adminProfiler.endMeasurement('08_ClearCache', {
                cacheCleared: true
            });

            // 📊 Finalizar sesión
            adminProfiler.endSession({
                success: true,
                clientesUpdated: adminData.clientesAsignados.length,
                tiendasUpdated: adminData.tiendasAsignadas.length
            });

            alert('Perfil actualizado correctamente');
            navigate(-1);
        } catch (err) {
            console.error('Error updating admin:', err);
            setError('Error al actualizar el usuario. Por favor, intenta de nuevo.');
            
            // Finalizar sesión con error
            adminProfiler.endSession({
                error: err.message,
                success: false
            });
        } finally {
            setLoading(false);
        }
    }, [adminData, newTipoUsuario, currentService, adminId, navigate]);

    const handleChangeTipoUsuario = useCallback((event) => {
        const { value } = event.target;
        setNewTipoUsuario(value);
    }, []);

    const handleChangeComunidades = useCallback((event) => {
        const comunidadesSeleccionadas = event.target.value;
        setComunidadesSeleccionadas(comunidadesSeleccionadas);

        const provinciasDisponibles = comunidadesSeleccionadas.reduce((acc, comunidad) => {
            return [...acc, ...(provinciasPorComunidad[comunidad] || [])];
        }, []);

        const nuevasProvinciasSeleccionadas = adminData.provinciasAsignadas.filter(provincia =>
            provinciasDisponibles.includes(provincia)
        );

        setProvinciasSeleccionadas(nuevasProvinciasSeleccionadas);

        setAdminData(prevAdminData => ({
            ...prevAdminData,
            comunidadesAsignadas: comunidadesSeleccionadas,
            provinciasAsignadas: nuevasProvinciasSeleccionadas,
        }));
    }, [adminData.provinciasAsignadas]);

    const updateClientesYTiendas = useCallback(async (userId, tipoUsuario, clientesAsignados, tiendasAsignadas) => {
        const esAdministrativo = tipoUsuario.includes('administrativo');
        const esInspector = tipoUsuario.includes('inspector');

        for (const clienteId of clientesAsignados) {
            const clienteRef = doc(db, 'servicios', currentService.id, 'clientes', clienteId);
            if (esAdministrativo) {
                await updateDoc(clienteRef, { administrativos: arrayUnion(userId) });
            }
            if (esInspector) {
                await updateDoc(clienteRef, { inspectores: arrayUnion(userId) });
            }
        }

        for (const tiendaId of tiendasAsignadas) {
            const tiendaRef = doc(db, 'servicios', currentService.id, 'tiendas', tiendaId);
            const tiendaData = (await getDoc(tiendaRef)).data();
            
            const esServicioExterno = false;
            
            if (esAdministrativo) {
                await updateDoc(tiendaRef, { 
                    administrativos: arrayUnion(userId),
                    ...(esServicioExterno && { 
                        administrativosExternos: arrayUnion(userId),
                        clientePrincipalAsignado: adminData.clientesAsignados[0] || null
                    })
                });
            }
            if (esInspector) {
                await updateDoc(tiendaRef, { 
                    inspectores: arrayUnion(userId),
                    ...(esServicioExterno && { 
                        inspectoresExternos: arrayUnion(userId),
                        clientePrincipalAsignado: adminData.clientesAsignados[0] || null
                    })
                });
            }
        }
    }, [currentService, adminData.clientesAsignados]);

    const handleSelectProvincias = useCallback((provinciasSeleccionadas) => {
        setProvinciasSeleccionadas(provinciasSeleccionadas);
        setAdminData(prev => ({ ...prev, provinciasAsignadas: provinciasSeleccionadas }));
    }, []);

    // Loading state
    if (initialLoading) {
        return (
            <div className="add-user-container">
                <h1>Editar Usuario</h1>
                <div style={{ padding: '20px' }}>
                    {[...Array(8)].map((_, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                            <Skeleton variant="rectangular" height={56} />
                        </Box>
                    ))}
                </div>
            </div>
        );
    }

    // Log del estado actual para debugging
    console.log('🔥 Estado actual EditAdmin:', {
        adminId,
        currentService: currentService?.id,
        adminData: {
            nombre: adminData.nombre,
            clientesAsignados: adminData.clientesAsignados?.length || 0,
            tiendasAsignadas: adminData.tiendasAsignadas?.length || 0
        },
        showClientes,
        showServicios,
        clientesDataLength: clientesData.length,
        serviciosDataLength: serviciosData.length,
        clientesSearchTerm,
        serviciosSearchTerm
    });

    // Error state
    if (error) {
        return (
            <div className="add-user-container">
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
                <Button 
                    variant="contained" 
                    onClick={() => window.location.reload()}
                    sx={{ mt: 2 }}
                >
                    Reintentar
                </Button>
            </div>
        );
    }

    return (
        <div className="add-user-container">
            <h1>Editar Usuario</h1>
            
            {/* Indicador de caché */}
            {isUsingCache && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        ⚡ Datos cargados desde caché - Carga instantánea
                    </Typography>
                </Alert>
            )}
            
            <div className="form-container">
                {/* Campos básicos */}
                <div className="form-control">
                    <TextField
                        label="Nombre"
                        value={adminData.nombre}
                        onChange={(e) => setAdminData({ ...adminData, nombre: e.target.value })}
                    />
                </div>
                <div className="form-control">
                    <TextField
                        label="Apellidos"
                        value={adminData.apellido}
                        onChange={(e) => setAdminData({ ...adminData, apellido: e.target.value })}
                    />
                </div>
                
                <FormControl fullWidth>
                    <InputLabel id="tipoUsuario-label">Tipo de Usuario</InputLabel>
                    <Select
                        labelId="tipoUsuario-label"
                        id="tipoUsuario"
                        multiple
                        value={newTipoUsuario.length > 0 ? newTipoUsuario : adminData.tipoUsuario}
                        onChange={handleChangeTipoUsuario}
                        label="Tipo de Usuario"
                    >
                        <MenuItem value="administrador">Administrador</MenuItem>
                        <MenuItem value="administrativo">Administrativo</MenuItem>
                        <MenuItem value="inspector">Inspector</MenuItem>
                    </Select>
                </FormControl>
                
                {/* Sección de Clientes con Lazy Loading */}
                <div style={{ marginBottom: '16px' }}>
                    <Button
                        variant="outlined"
                        onClick={() => setShowClientes(!showClientes)}
                        endIcon={showClientes ? <ExpandLess /> : <ExpandMore />}
                        fullWidth
                        style={{ 
                            justifyContent: 'space-between', 
                            textTransform: 'none',
                            borderColor: adminData.clientesAsignados.length > 0 ? '#1976d2' : '#ddd',
                            backgroundColor: adminData.clientesAsignados.length > 0 ? '#f0f8ff' : 'transparent'
                        }}
                    >
                        <span>Clientes Asignados</span>
                        <span style={{ 
                            fontSize: '12px', 
                            color: adminData.clientesAsignados.length > 0 ? '#1976d2' : '#666',
                            fontWeight: adminData.clientesAsignados.length > 0 ? '500' : 'normal'
                        }}>
                            {adminData.clientesAsignados.length} seleccionados
                        </span>
                    </Button>
                    
                    <Collapse in={showClientes}>
                        <div style={{ marginTop: '12px', padding: '12px', border: '1px solid #ddd', borderRadius: '4px' }}>
                            {/* Campo de búsqueda */}
                            <TextField
                                fullWidth
                                placeholder="Buscar clientes por nombre..."
                                value={clientesSearchTerm}
                                onChange={(e) => setClientesSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search />
                                        </InputAdornment>
                                    ),
                                    endAdornment: clientesSearchTerm && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => setClientesSearchTerm('')}
                                                edge="end"
                                            >
                                                <Clear />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                                size="small"
                                style={{ marginBottom: '12px' }}
                            />
                            
                            {/* Información de resultados */}
                            <div style={{ 
                                marginBottom: '12px', 
                                padding: '8px', 
                                backgroundColor: '#f5f5f5', 
                                borderRadius: '4px',
                                fontSize: '12px',
                                color: '#666'
                            }}>
                                {clientesSearchDebounce 
                                    ? `${clientesMostrados.length} clientes encontrados • ${adminData.clientesAsignados.length} asignados`
                                    : `${adminData.clientesAsignados.length} clientes asignados • Usa la búsqueda para agregar más clientes`
                                }
                            </div>
                            
                            {/* Opciones de selección múltiple */}
                            <div style={{ marginBottom: '12px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <Typography variant="caption" style={{ color: '#666', fontWeight: '500' }}>
                                        Selección múltiple:
                                    </Typography>
                                </div>
                                
                                {/* Opción 1: Seleccionar todos los clientes encontrados */}
                                {clientesSearchDebounce && (
                                    <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Checkbox
                                            checked={clientesMostrados.length > 0 && clientesMostrados.every(c => c.isAsignado)}
                                            indeterminate={clientesMostrados.some(c => c.isAsignado) && !clientesMostrados.every(c => c.isAsignado)}
                                            onChange={(e) => handleSelectAllClientes(e.target.checked)}
                                            size="small"
                                        />
                                        <span style={{ fontSize: '13px' }}>
                                            Seleccionar todos los clientes encontrados ({clientesMostrados.length})
                                        </span>
                                    </div>
                                )}
                                
                                {/* Opción 2: Asignar TODOS los clientes de la base de datos */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Checkbox
                                        checked={adminData.clientesAsignados.length > 0 && clientesData.length > 0 && clientesData.every(c => c.isAsignado)}
                                        onChange={(e) => handleAssignAllClientes(e.target.checked)}
                                        size="small"
                                    />
                                    <span style={{ fontSize: '13px', color: '#d32f2f' }}>
                                        🚀 Asignar TODOS los clientes de la base de datos
                                    </span>
                                </div>
                            </div>
                            
                            {/* Lista de clientes */}
                            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                {clientesMostrados.map((cliente) => (
                                    <div key={cliente.id} style={{ 
                                        padding: '8px', 
                                        borderBottom: '1px solid #eee',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        backgroundColor: cliente.isAsignado ? '#f0f8ff' : 'transparent',
                                        borderLeft: cliente.isAsignado ? '3px solid #1976d2' : 'none'
                                    }}>
                                        <Checkbox
                                            checked={cliente.isAsignado}
                                            onChange={(e) => handleClienteToggle(cliente.id, e.target.checked)}
                                            size="small"
                                        />
                                        <div style={{ flex: 1 }}>
                                            <span style={{ 
                                                fontSize: '13px',
                                                fontWeight: cliente.isAsignado ? '500' : 'normal',
                                                color: cliente.isAsignado ? '#1976d2' : 'inherit'
                                            }}>
                                                {cliente.nombre}
                                                {cliente.comunidad && ` (${cliente.comunidad})`}
                                            </span>
                                            {cliente.isAsignado && (
                                                <div style={{ 
                                                    fontSize: '11px', 
                                                    color: '#1976d2',
                                                    marginTop: '2px'
                                                }}>
                                                    ✓ Asignado
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                
                                {/* Loading indicator */}
                                {clientesLoading && (
                                    <div style={{ textAlign: 'center', padding: '12px' }}>
                                        <CircularProgress size={20} />
                                        <Typography variant="caption" display="block">
                                            Cargando más clientes...
                                        </Typography>
                                    </div>
                                )}
                                
                                {/* Observer target para infinite scroll */}
                                {clientesHasMore && !clientesLoading && (
                                    <div ref={clientesObserverRef} style={{ height: '20px' }} />
                                )}
                                
                                {/* Mensaje de fin */}
                                {!clientesHasMore && clientesMostrados.length > 0 && (
                                    <div style={{ 
                                        textAlign: 'center', 
                                        padding: '12px', 
                                        color: '#666',
                                        fontSize: '12px',
                                        borderTop: '1px solid #eee',
                                        marginTop: '8px'
                                    }}>
                                        No hay más clientes que mostrar
                                    </div>
                                )}
                                
                                {/* Mensaje cuando no hay datos */}
                                {clientesMostrados.length === 0 && !clientesLoading && (
                                    <div style={{ 
                                        textAlign: 'center', 
                                        padding: '40px', 
                                        color: '#666',
                                        fontSize: '14px'
                                    }}>
                                        {clientesSearchDebounce 
                                            ? `No se encontraron clientes que coincidan con "${clientesSearchDebounce}"`
                                            : "Busca clientes para ver los disponibles"
                                        }
                                    </div>
                                )}
                            </div>
                        </div>
                    </Collapse>
                </div>
                
                {/* Sección de Servicios con Lazy Loading */}
                <div style={{ marginBottom: '16px' }}>
                    <Button
                        variant="outlined"
                        onClick={() => setShowServicios(!showServicios)}
                        endIcon={showServicios ? <ExpandLess /> : <ExpandMore />}
                        fullWidth
                        style={{ 
                            justifyContent: 'space-between', 
                            textTransform: 'none',
                            borderColor: adminData.tiendasAsignadas.length > 0 ? '#1976d2' : '#ddd',
                            backgroundColor: adminData.tiendasAsignadas.length > 0 ? '#f0f8ff' : 'transparent'
                        }}
                    >
                        <span>Servicios Asignados</span>
                        <span style={{ 
                            fontSize: '12px', 
                            color: adminData.tiendasAsignadas.length > 0 ? '#1976d2' : '#666',
                            fontWeight: adminData.tiendasAsignadas.length > 0 ? '500' : 'normal'
                        }}>
                            {adminData.tiendasAsignadas.length} seleccionados
                        </span>
                    </Button>
                    
                    <Collapse in={showServicios}>
                        <div style={{ marginTop: '12px', padding: '12px', border: '1px solid #ddd', borderRadius: '4px' }}>
                            {/* Campo de búsqueda */}
                            <TextField
                                fullWidth
                                placeholder="Buscar servicios por número o nombre..."
                                value={serviciosSearchTerm}
                                onChange={(e) => setServiciosSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search />
                                        </InputAdornment>
                                    ),
                                    endAdornment: serviciosSearchTerm && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => setServiciosSearchTerm('')}
                                                edge="end"
                                            >
                                                <Clear />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                                size="small"
                                style={{ marginBottom: '12px' }}
                            />
                            
                            {/* Información de resultados */}
                            <div style={{ 
                                marginBottom: '12px', 
                                padding: '8px', 
                                backgroundColor: '#f5f5f5', 
                                borderRadius: '4px',
                                fontSize: '12px',
                                color: '#666'
                            }}>
                                {serviciosSearchDebounce 
                                    ? `${serviciosMostrados.length} servicios encontrados • ${adminData.tiendasAsignadas.length} asignados`
                                    : `${adminData.tiendasAsignadas.length} servicios asignados • Usa la búsqueda para agregar más servicios`
                                }
                            </div>
                            
                            {/* Toggle de visualización Localización/Nombre */}
                            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Mostrar:
                                </Typography>
                                <ToggleButtonGroup
                                    value={serviciosDisplayMode}
                                    exclusive
                                    onChange={handleServiciosDisplayModeChange}
                                    size="small"
                                >
                                    <ToggleButton value="localizacion" sx={{ textTransform: 'none', px: 2 }}>
                                        Localización
                                    </ToggleButton>
                                    <ToggleButton value="nombre" sx={{ textTransform: 'none', px: 2 }}>
                                        Nombre
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>

                            {/* Opciones de selección múltiple */}
                            <div style={{ marginBottom: '12px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <Typography variant="caption" style={{ color: '#666', fontWeight: '500' }}>
                                        Selección múltiple:
                                    </Typography>
                                </div>
                                
                                {/* Opción 1: Seleccionar todos los servicios encontrados */}
                                {serviciosSearchDebounce && (
                                    <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Checkbox
                                            checked={serviciosMostrados.length > 0 && serviciosMostrados.every(s => s.isAsignado)}
                                            indeterminate={serviciosMostrados.some(s => s.isAsignado) && !serviciosMostrados.every(s => s.isAsignado)}
                                            onChange={(e) => handleSelectAllServicios(e.target.checked)}
                                            size="small"
                                        />
                                        <span style={{ fontSize: '13px' }}>
                                            Seleccionar todos los servicios encontrados ({serviciosMostrados.length})
                                        </span>
                                    </div>
                                )}
                                
                                {/* Opción 2: Asignar TODOS los servicios de la base de datos */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Checkbox
                                        checked={adminData.tiendasAsignadas.length > 0 && serviciosData.length > 0 && serviciosData.every(s => s.isAsignado)}
                                        onChange={(e) => handleAssignAllServicios(e.target.checked)}
                                        size="small"
                                    />
                                    <span style={{ fontSize: '13px', color: '#d32f2f' }}>
                                        🚀 Asignar TODOS los servicios de la base de datos
                                    </span>
                                </div>
                            </div>
                            
                            {/* Lista de servicios */}
                            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                {serviciosMostrados.map((servicio) => (
                                    <div key={servicio.id} style={{ 
                                        padding: '8px', 
                                        borderBottom: '1px solid #eee',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        backgroundColor: servicio.isAsignado ? '#f0f8ff' : 'transparent',
                                        borderLeft: servicio.isAsignado ? '3px solid #1976d2' : 'none'
                                    }}>
                                        <Checkbox
                                            checked={servicio.isAsignado}
                                            onChange={(e) => handleServicioToggle(servicio.id, e.target.checked)}
                                            size="small"
                                        />
                                        <div style={{ flex: 1 }}>
                                            <span style={{ 
                                                fontSize: '13px',
                                                fontWeight: servicio.isAsignado ? '500' : 'normal',
                                                color: servicio.isAsignado ? '#1976d2' : 'inherit'
                                            }}>
                                                Servicio {serviciosDisplayMode === 'localizacion' && servicio.localizacion 
                                                    ? servicio.localizacion 
                                                    : servicio.numTienda} - {servicio.nombreTienda}
                                            </span>
                                            <span style={{ fontSize: '11px', color: '#666' }}>
                                                {` (${servicio.nombreCliente})`}
                                            </span>
                                            {serviciosDisplayMode === 'nombre' && servicio.localizacion && (
                                                <span style={{ fontSize: '10px', color: '#888', marginLeft: '8px' }}>
                                                    📍 {servicio.localizacion}
                                                </span>
                                            )}
                                            {servicio.isAsignado && (
                                                <div style={{ 
                                                    fontSize: '11px', 
                                                    color: '#1976d2',
                                                    marginTop: '2px'
                                                }}>
                                                    ✓ Asignado
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                
                                {/* Loading indicator */}
                                {serviciosLoading && (
                                    <div style={{ textAlign: 'center', padding: '12px' }}>
                                        <CircularProgress size={20} />
                                        <Typography variant="caption" display="block">
                                            Cargando más servicios...
                                        </Typography>
                                    </div>
                                )}
                                
                                {/* Observer target para infinite scroll */}
                                {serviciosHasMore && !serviciosLoading && (
                                    <div ref={serviciosObserverRef} style={{ height: '20px' }} />
                                )}
                                
                                {/* Mensaje de fin */}
                                {!serviciosHasMore && serviciosMostrados.length > 0 && (
                                    <div style={{ 
                                        textAlign: 'center', 
                                        padding: '12px', 
                                        color: '#666',
                                        fontSize: '12px',
                                        borderTop: '1px solid #eee',
                                        marginTop: '8px'
                                    }}>
                                        No hay más servicios que mostrar
                                    </div>
                                )}
                                
                                {/* Mensaje cuando no hay datos */}
                                {serviciosMostrados.length === 0 && !serviciosLoading && (
                                    <div style={{ 
                                        textAlign: 'center', 
                                        padding: '40px', 
                                        color: '#666',
                                        fontSize: '14px'
                                    }}>
                                        {serviciosSearchDebounce 
                                            ? `No se encontraron servicios que coincidan con "${serviciosSearchDebounce}"`
                                            : "Busca servicios para ver los disponibles"
                                        }
                                    </div>
                                )}
                            </div>
                        </div>
                    </Collapse>
                </div>
                

                
                {/* Campos de comunidades y provincias */}
                <FormControl fullWidth>
                    <InputLabel id="comunidades-label">Comunidades</InputLabel>
                    <Select
                        labelId="comunidades-label"
                        id="comunidades"
                        multiple
                        value={adminData.comunidadesAsignadas}
                        onChange={handleChangeComunidades}
                        label="Comunidades"
                    >
                        {comunidades.map((comunidad) => (
                            <MenuItem key={comunidad} value={comunidad}>
                                {comunidad}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                
                <ProvinciasSelect
                    comunidadesSeleccionadas={comunidadesSeleccionadas}
                    provinciasSeleccionadas={provinciasSeleccionadas}
                    onSelectProvincias={handleSelectProvincias}
                />
            </div>
            
            {/* Botón de actualización */}
            <Button 
                onClick={handleUpdate} 
                variant="contained" 
                color="primary" 
                style={{ margin: '20px' }}
                disabled={loading}
            >
                {loading ? (
                    <>
                        Actualizando usuario...
                        <CircularProgress size={20} style={{ marginLeft: '10px', color: '#fff' }} />
                    </>
                ) : (
                    'Actualizar'
                )}
            </Button>
        </div>
    );
};

EditAdmin.propTypes = {
    adminId: PropTypes.string.isRequired
};

export default EditAdmin;